/* eslint-disable camelcase */
import { AxiosError } from 'axios';
import { useSWRConfig } from 'swr';
import useSWRMutation, { TriggerWithoutArgs } from 'swr/mutation';

import {
  isTruthy,
  PaymentErrorResponseT,
  PaymentPostDataT,
  PaymentReqDataT,
  PaymentSuccessResponseT,
  SwrKeyT,
  WithStatusT,
} from '@consumer-frontend/nc-utils';

import {
  apiEndpoints,
  dashboardFetchers,
} from '../lib';

export type UseMakePaymentPropsT = {
  accountId: string;
  loanNumber?: string;
  paymentDetails?: PaymentReqDataT;
};

export type UseMakePaymentRespT = {
  isMutating: boolean;
  reset: () => void;
  trigger: TriggerWithoutArgs<
    WithStatusT<PaymentSuccessResponseT>,
    AxiosError<PaymentErrorResponseT, unknown>,
    SwrKeyT
  >
;
  error?: AxiosError<PaymentErrorResponseT>;
  makePaymentResp?: {
    successType: PaymentSuccessResponseT['success_type'];
  };
  status?: number;
};

export const useMakePayment = ({
  accountId,
  loanNumber,
  paymentDetails,
}: UseMakePaymentPropsT): UseMakePaymentRespT => {
  const { mutate } = useSWRConfig();

  // We need to call each of these individually rather than something like `props.every(isTruthy)`
  // in order for the types to be properly narrowed by TypeScript.
  const isReadyToFetch = isTruthy(accountId) && isTruthy(loanNumber) && isTruthy(paymentDetails);

  const {
    paymentAmount: payment_amount,
    paymentDate,
    paymentMethod: payment_method,
  } = paymentDetails || {} as PaymentReqDataT;

  const postData: PaymentPostDataT = {
    date: paymentDate?.toString() || '',
    payment_amount,
    payment_method,
  };

  const {
    data,
    error,
    reset,
    trigger,
    isMutating,
  } = useSWRMutation<
    WithStatusT<PaymentSuccessResponseT>,
    AxiosError<PaymentErrorResponseT>,
    SwrKeyT
  >(
    () => (isReadyToFetch ? apiEndpoints.dashboard.makePayment(accountId, loanNumber) : null),
    (url: string) => dashboardFetchers.post<PaymentPostDataT, PaymentSuccessResponseT>(url, { arg: postData }),
    {
      onSuccess: async () => {
        if (accountId && loanNumber) {
          mutate(apiEndpoints.dashboard.loanSummary(accountId, loanNumber));
          mutate(apiEndpoints.dashboard.paymentAmountDue(accountId, loanNumber));
          mutate(apiEndpoints.dashboard.fullPayoffAmount(accountId, loanNumber));
          mutate(apiEndpoints.dashboard.scheduledPayments(accountId, loanNumber));
        }
      },
    },
  );

  return {
    error,
    isMutating,
    makePaymentResp: data
      ? { successType: data.success_type }
      : undefined,
    reset,
    status: data?.status || error?.status,
    trigger,
  };
};
